import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Mail, Copy, Check, Clock, Key, ArrowRight } from "lucide-react";

function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  let s = String(dateStr);
  s = s.replace(/[Zz]$/, "").replace(/\+00:?00$/, "");
  s = s.replace(/(\.\d{3})\d*$/, "$1");
  if (!s.endsWith("Z")) s += "Z";
  return new Date(s);
}

export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/free/share/${token}`);
        setData(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetch();
  }, [token]);

  const handleCopy = async () => {
    if (!data?.alias_address) return;
    try {
      await navigator.clipboard.writeText(data.alias_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Email address copied");
    } catch {
      // Fallback
      const el = document.createElement("textarea");
      el.value = data.alias_address;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTimeLeft = () => {
    if (!data?.expires_at) return null;
    const diff = parseUTC(data.expires_at).getTime() - Date.now();
    if (diff <= 0) return "Expired";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (days > 1) return `Expires in ${days} days`;
    if (hours > 1) return `Expires in ${hours} hours`;
    const minutes = Math.floor(diff / (1000 * 60));
    return `Expires in ${minutes} min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="share-not-found">
        <div className="max-w-sm text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F5F5F5] flex items-center justify-center">
            <Mail size={28} className="text-zinc-500" />
          </div>
          <h1 className="font-heading text-xl font-medium text-[#111111]">Link not found</h1>
          <p className="text-sm text-zinc-500">This share link is invalid or has expired.</p>
          <Link to="/?new=1">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">Create a new email</Button>
          </Link>
        </div>
      </div>
    );
  }

  const timeLeft = getTimeLeft();

  return (
    <div className="min-h-screen bg-white" data-testid="share-page">
      {/* Header */}
      <header className="border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/?new=1" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
              <Mail size={14} className="text-white" strokeWidth={2} />
            </div>
            <span className="font-heading text-lg font-medium tracking-tight text-[#111111]">QuickInbox</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-md mx-auto px-4 py-16 sm:py-24 text-center space-y-6">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-50 flex items-center justify-center">
          <Mail size={28} className="text-blue-600" />
        </div>

        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Temporary email</p>
          <div
            className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-4 cursor-pointer hover:border-[#E5E5E5] transition-colors"
            onClick={handleCopy}
            data-testid="share-email-display"
          >
            <code className="text-lg sm:text-xl font-mono font-medium text-[#111111] break-all" data-testid="share-email-address">
              {data.alias_address}
            </code>
          </div>
        </div>

        {timeLeft && (
          <div className="flex items-center justify-center gap-1.5 text-xs text-zinc-500" data-testid="share-expiration">
            <Clock size={12} className="text-zinc-500" />
            <span>{timeLeft}</span>
          </div>
        )}

        <div className="space-y-3 pt-2">
          <Button
            onClick={handleCopy}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="share-copy-button"
          >
            {copied ? <><Check size={16} className="mr-2" /> Copied!</> : <><Copy size={16} className="mr-2" /> Copy email address</>}
          </Button>

          <Link to="/claim" data-testid="share-access-inbox-link">
            <Button variant="outline" className="w-full h-11 mt-2">
              <Key size={14} className="mr-2" /> Access inbox <ArrowRight size={14} className="ml-1" />
            </Button>
          </Link>
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed">
          This is a shared email address. To access the inbox, you need the access code that was given when the email was created.
        </p>
      </div>
    </div>
  );
}
